import { useZoneCrossings } from '@hakimo-ui/hakimo/data-access';
import { PresenceZone } from '@hakimo-ui/hakimo/types';
import { CrossingsChart } from '@hakimo-ui/hakimo/ui-elements';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

interface Props {
  zones: PresenceZone[];
  /** The occupancy payload's clock; the day window rolls forward with it */
  nowUtc?: string;
}

/**
 * Crossings of each line over the last day, each direction apart, and the
 * inferred appearances and vanishes apart from both. Those are recorded on
 * every line whether or not a rule counts them, which is what makes "should
 * this door's rule opt into them" a question the chart can answer.
 */
export function CrossingsCard({ zones, nowUtc }: Props) {
  const lines = useMemo(() => zones.filter((z) => z.kind === 'line'), [zones]);
  const [chosenId, setLineId] = useState<string | null>(null);
  // A chosen line that has since been deleted falls back to the first.
  const selected = lines.find((l) => l.id === chosenId) ?? lines[0];
  const lineId = selected?.id ?? null;

  const since = useMemo(
    () => dayjs().subtract(24, 'hour').startOf('hour').toISOString(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nowUtc]
  );
  const crossings = useZoneCrossings(
    lineId ?? '',
    { from: since, bucket: '1h' },
    !!lineId
  );

  const totals = crossings.data?.totals;
  return (
    <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 space-y-4 rounded-lg p-4">
      <div className="flex flex-wrap gap-2">
        {lines.map((line) => (
          <button
            key={line.id}
            type="button"
            onClick={() => setLineId(line.id)}
            className={clsx(
              'rounded-full border px-3 py-1 text-sm',
              line.id === lineId
                ? 'border-sky-600 bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'
                : 'border-onlight-line-2 dark:border-ondark-line-2 text-onlight-text-2 dark:text-ondark-text-2'
            )}
          >
            {line.name}
          </button>
        ))}
      </div>

      {selected && (
        <div>
          <h4 className="text-onlight-text-1 dark:text-ondark-text-1 mb-1 text-sm font-semibold">
            {selected.name} · last 24 hours
          </h4>
          {totals && (
            <p className="text-onlight-text-2 dark:text-ondark-text-2 mb-2 flex flex-wrap gap-x-4 text-xs">
              <span>
                <span className="mr-1 inline-block h-2 w-2 rounded-sm bg-sky-600" />
                {totals.ltr} in the arrow's direction
              </span>
              <span>
                <span className="mr-1 inline-block h-2 w-2 rounded-sm bg-amber-500" />
                {totals.rtl} against the arrow
              </span>
              <span>
                <span className="border-onlight-text-3 dark:border-ondark-text-3 mr-1 inline-block h-2 w-2 rounded-sm border border-dashed" />
                {totals.inferred} inferred (appeared or vanished at the line)
              </span>
            </p>
          )}
          {crossings.data ? (
            <CrossingsChart crossings={crossings.data.items} />
          ) : (
            <p className="text-onlight-text-3 dark:text-ondark-text-3 text-sm">
              {crossings.isError
                ? crossings.error?.message
                : 'Loading the day…'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default CrossingsCard;
